import { HEADER_HEIGHT } from "@/constants/components";
import { PRIME } from "@/constants/productKey";
import { useActivePathCheck } from "@/hooks/useActivePathCheck";
import { sessionStorage, storageKeys } from "@/utils/storage";
import { Box, Stack } from "@chakra-ui/react";

import {
  colors,
  HStack,
  GoiLogo,
  Spacing,
  GLOBAL_MAX_WIDTH,
  NextLink,
  customEvent,
  useResponsive,
  DirectEventTriggerAndTargetType,
  TabBar,
  responsive,
  Button,
  VStack,
} from "goi_common";
import { useRouter } from "next/router";

export default function BeforeHeader({ onOpen }: { onOpen: () => void }) {
  const { isBiggerThanLg } = useResponsive();
  const router = useRouter();

  const isBasicLiv =
    sessionStorage.get(storageKeys.ProductKey) === PRIME[199].M1;
  const isBasicLivProcess = router.route.includes("basic-liv") || isBasicLiv;

  const isSelfFuneral = router.route.includes("self-funeral");
  const isOrganizationFuneral = router.route.includes("organization-funeral");

  const isBenefitTypeOverseas =
    sessionStorage.get(storageKeys.benefitType) === "overseas";
  const isOverseas = router.route.includes("overseas") || isBenefitTypeOverseas;

  const isHeaderContentHiddenRoute =
    router.route.includes("registration") ||
    router.route.includes("calculator") ||
    isBasicLivProcess ||
    isSelfFuneral ||
    isOrganizationFuneral ||
    isOverseas;

  return (
    <>
      <VStack
        css={responsive({
          height: {
            base: isHeaderContentHiddenRoute ? 66 : 102,
            lg: HEADER_HEIGHT,
          },
          borderBottom: `1px solid ${colors.gray100}`,
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto",
          position: "fixed",
          width: "100%",
          top: "0",
          backgroundColor: colors.white,
          transition: "background-color 0.1s linear",
          zIndex: "1000",
          userSelect: "none",
        })}
      >
        <Stack
          css={responsive({
            flex: 1,
            width: { base: "100%", lg: `${GLOBAL_MAX_WIDTH}px` },
            flexDirection: { base: "column", lg: "row" },
            justifyContent: { base: "space-between", lg: "" },
            alignItems: { base: "", lg: "center" },
            padding: { base: "16px 20px 0", lg: "0px 20px" },
          })}
        >
          <HStack
            alignItems="center"
            justifyContent="space-between"
            gap={isBiggerThanLg ? "36px" : "20px"}
          >
            <NextLink
              id="goi-logo"
              href={
                isBasicLivProcess
                  ? "/basic-liv/"
                  : isSelfFuneral
                  ? "/self-funeral/"
                  : isOrganizationFuneral
                  ? "/organization-funeral/"
                  : isOverseas
                  ? "/overseas/"
                  : "/"
              }
              css={{ width: "fit-content" }}
              onClick={() => {
                customEvent({
                  newGtm: {
                    path: "gnb",
                    trigger_and_target: "logo-uv_home",
                  },
                });
              }}
            >
              <HStack gap={isBiggerThanLg ? "12px" : "8px"} alignItems="center">
                <GoiLogo color={"black"} size={isBiggerThanLg ? 30 : 24} />
                <div className={"subtitle_18_b md_subtitle_21_b black"}>
                  PRIME
                </div>
              </HStack>
            </NextLink>
            {!isHeaderContentHiddenRoute && (
              <Button
                className="subtitle_14_b lg_over_hidden"
                css={{
                  width: "95px",
                  padding: "6px 12px",
                  backgroundColor: colors.orange650,
                  color: "white",
                  borderRadius: "5px",
                  border: "1px solid white",
                }}
                onClick={() => {
                  customEvent({
                    newGtm: {
                      path: "gnb",
                      trigger_and_target: "contract_check",
                    },
                  });
                  router.push("/contract-check/");
                }}
              >
                계약서 확인
              </Button>
            )}
          </HStack>
          <HStack
            alignItems="center"
            justifyContent="center"
            gap={isBiggerThanLg ? "36px" : "20px"}
          >
            {!isHeaderContentHiddenRoute && (
              <>
                <HeaderTabBar isHeaderTransparents={false} />
                <Button
                  className="subtitle_14_b lg_under_hidden"
                  css={{
                    width: "112px",
                    padding: "6px 12px",
                    backgroundColor: colors.orange650,
                    color: "white",
                    borderRadius: "5px",
                    border: "1px solid white",
                  }}
                  onClick={() => {
                    customEvent({
                      newGtm: {
                        path: "gnb",
                        trigger_and_target: "contract_check",
                      },
                    });
                    router.push("/contract-check/");
                  }}
                >
                  계약서 확인
                </Button>
              </>
            )}
          </HStack>
        </Stack>
      </VStack>

      <Spacing
        gap={
          isBiggerThanLg ? HEADER_HEIGHT : isHeaderContentHiddenRoute ? 66 : 102
        }
      />
    </>
  );
}

function HeaderTabBar({
  isHeaderTransparents,
}: {
  isHeaderTransparents: boolean;
}) {
  const { activePath } = useActivePathCheck();
  const { isBiggerThanLg } = useResponsive();

  const menu: Array<{
    text: string;
    to: string;
    eventDetails: DirectEventTriggerAndTargetType["gnb"][];
    onClick?: () => void;
  }> = [
    {
      text: "홈",
      to: "/",
      eventDetails: ["uv_home"],
    },
    {
      text: "자주 묻는 질문",
      to: "/faq/",
      eventDetails: ["uv_faq"],
    },
    {
      text: "이벤트",
      to: "/promotion/",
      eventDetails: ["uv_b2b"],
    },
    // {
    //   text: "기업 고객",
    //   to: "https://partners.goifuneral.co.kr/",
    //   eventDetails: ["uv_b2b"],
    // },
    {
      text: "고이 소개",
      to: "/introduce/",
      eventDetails: ["uv_intro"],
    },
  ];

  return (
    <HStack
      css={responsive({
        height: { base: "40px", lg: "100%" },
      })}
      width="100%"
      justifyContent="space-between"
      gap={isBiggerThanLg ? "50px" : "0"}
    >
      {menu.map((item, i) => {
        return (
          <Box position="relative" key={i}>
            {item.text === "이벤트" && (
              <Box
                position="absolute"
                top="-7px"
                right="-10px"
                width="12px"
                height="12px"
                lineHeight="12px"
                fontSize="9px"
                fontWeight="bold"
                textAlign="center"
                backgroundColor="red"
                borderRadius="50%"
                color="white"
                zIndex="10"
              >
                N
              </Box>
            )}
            <NextLink href={item.to}>
              <TabBar.Tab
                className={
                  isHeaderTransparents
                    ? "subtitle_18_sb white"
                    : "subtitle_18_sb gray500"
                }
                onClick={() => {
                  if (item.onClick) {
                    item.onClick();
                  }
                  item.eventDetails.map((trigger_and_target) => {
                    customEvent({
                      newGtm: {
                        path: "gnb",
                        trigger_and_target,
                      },
                    });
                  });
                }}
                isActive={activePath === item.text}
                activeFontStyle={{
                  color: isHeaderTransparents ? colors.white : colors.gray900,
                }}
                activeBarStyle={{
                  borderBottom: isBiggerThanLg
                    ? "none"
                    : isHeaderTransparents
                    ? "3px solid white"
                    : "3px solid black",
                }}
                css={responsive({
                  height: { base: "40px", lg: "fit-content" },
                })}
              >
                {item.text}
              </TabBar.Tab>
            </NextLink>
          </Box>
        );
      })}
    </HStack>
  );
}
